/* Number and date formatting, in one place so a column reads the same wherever
 * it appears. */

const nf = new Intl.NumberFormat('en-US');

export function num(n: number): string {
  return nf.format(Math.round(n));
}

/** Token counts, in the design's own shorthand: 4.2M / 252k / 900. */
export function tokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}k`;
  return String(Math.round(n));
}

/** Provider LIST price. Never what an estate charged — the router does not
 *  price, and every call site that renders this labels it as list. */
export function listCost(usd: number): string {
  return `$${nf.format(Math.round(usd))}`;
}

export function pct(fraction: number, digits = 0): string {
  return `${(fraction * 100).toFixed(digits)}%`;
}

/** quotaPct renders an ALREADY-percentage value (0–100), unlike pct which takes
 *  a fraction.
 *
 *  It rounds, because these arrive from provider arithmetic and land on values
 *  like 55.00000000000001 — a float artefact that is not a measurement, and
 *  printing it raw makes the console look like it cannot count. */
export function quotaPct(percent: number): string {
  const r = Math.round(percent * 10) / 10;
  return `${Number.isInteger(r) ? r.toFixed(0) : r.toFixed(1)}%`;
}

export function latency(ms: number): string {
  if (ms >= 10_000) return `${(ms / 1000).toFixed(0)}s`;
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms)}ms`;
}

export function dateTime(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function dateOnly(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US');
}

/** The ledger's own timestamp column: time-of-day to the millisecond, because
 *  two rows in the same second is normal and the operator is scanning for one. */
export function clockMs(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const p = (n: number, w = 2) => String(n).padStart(w, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}.${p(d.getMilliseconds(), 3)}`;
}

export function relative(iso?: string | null): string {
  if (!iso) return 'never';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return 'never';
  const secs = Math.round((Date.now() - then) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)} min ago`;
  if (secs < 86_400) return `${Math.round(secs / 3600)} hours ago`;
  return `${Math.round(secs / 86_400)} days ago`;
}

/** True when a date is close enough that an operator should act. Used to tint
 *  token and key expiry columns. */
export function expiringSoon(iso?: string | null, days = 7): boolean {
  if (!iso) return false;
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return false;
  return at - Date.now() < days * 86_400_000;
}
